import { forwardRef } from 'react';
import { useEditor } from '../../context/EditorContext';
import {
    RiDragMoveLine,
    RiExternalLinkLine,
    RiMailLine,
    RiPhoneLine,
    RiMapPinLine,
    RiLinkedinBoxFill,
    RiGithubFill,
    RiGlobalLine
} from 'react-icons/ri';
import type {
    Section,
    Item,
    HeaderItem,
    ParagraphItem,
    BlockItem,
    ListItem,
    NormalizedResume,
    SkillGroup,
    GroupedListItem
} from '../../models/resume-draft';

const ResumeCanvas = forwardRef<HTMLDivElement>((props, ref) => {
    const { currentVersion, updateResume } = useEditor();

    if (!currentVersion || !currentVersion.resume_json) return null;

    const resume = currentVersion.resume_json;
    const sections = resume.sections || [];

    // --- GENERIC UPDATE HANDLER ---
    // Updates a specific field in a specific item in a specific section
    const updateItemField = (sectionIndex: number, itemIndex: number, field: keyof Item | string, value: any) => {
        const updatedResume: NormalizedResume = JSON.parse(JSON.stringify(resume));
        if (updatedResume.sections && updatedResume.sections[sectionIndex]) {
            const section = updatedResume.sections[sectionIndex];
            if (section.items[itemIndex]) {
                (section.items[itemIndex] as any)[field] = value;
            }
        }
        updateResume(updatedResume, 'manual_edit');
    };

    const updateBulletText = (sectionIndex: number, itemIndex: number, bulletIndex: number, value: string) => {
        const updatedResume: NormalizedResume = JSON.parse(JSON.stringify(resume));
        const item = updatedResume.sections[sectionIndex].items[itemIndex] as BlockItem;
        if (item.bullets && item.bullets[bulletIndex]) {
            item.bullets[bulletIndex].text = value;
        }
        updateResume(updatedResume, 'manual_edit');
    };

    // New handler for grouped items
    const updateGroupItemText = (sectionIndex: number, groupIndex: number, itemIndex: number, value: string) => {
        const updatedResume: NormalizedResume = JSON.parse(JSON.stringify(resume));
        const section = updatedResume.sections[sectionIndex];
        if (section.groups && section.groups[groupIndex] && section.groups[groupIndex].items[itemIndex]) {
            section.groups[groupIndex].items[itemIndex].text = value;
        }
        updateResume(updatedResume, 'manual_edit');
    };

    // --- HELPER: Render External Link ---
    const renderExternalLink = (link?: string) => {
        if (!link) return null;
        return (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block ml-1 text-blue-500 hover:text-blue-700 align-middle"
                contentEditable={false} // Prevent editing the icon itself
                title={`Open link: ${link}`}
            >
                <RiExternalLinkLine size={12} />
            </a>
        );
    };

    // --- HELPER: Link Formatting & Icons ---
    const getIconForField = (field: string) => {
        const lower = field.toLowerCase();
        if (lower.includes('email')) return <RiMailLine />;
        if (lower.includes('phone')) return <RiPhoneLine />;
        if (lower.includes('location') || lower.includes('address')) return <RiMapPinLine />;
        if (lower.includes('linkedin')) return <RiLinkedinBoxFill />;
        if (lower.includes('github')) return <RiGithubFill />;
        return <RiGlobalLine />;
    };

    const formatLinkDisplay = (field: string, value: string) => {
        const lower = field.toLowerCase();

        // If it's a URL, try to clean it
        if (value.startsWith('http') || value.startsWith('www')) {
            try {
                // Simplify LinkedIn
                if (lower.includes('linkedin')) {
                    // Extract name from /in/name
                    const match = value.match(/linkedin\.com\/in\/([^/]+)/);
                    if (match && match[1]) return match[1];
                }
                // Simplify GitHub
                if (lower.includes('github')) {
                    // Extract username from github.com/username
                    const match = value.match(/github\.com\/([^/]+)/);
                    if (match && match[1]) return match[1];
                }

                // General fallback: remove protocol and www
                return value.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
            } catch (e) {
                return value;
            }
        }

        return value;
    };

    // --- RENDERERS BY CONTENT TYPE ---

    // A. key_value_pairs (HEADER)
    const renderKeyValuePairs = (section: Section, sIdx: number) => {
        // We need to identify specific fields to layout them correctly
        const items = section.items as HeaderItem[];

        const nameItem = items.find(i => i.field.toLowerCase() === 'name');
        const roleItem = items.find(i => i.field.toLowerCase() === 'role' || i.field.toLowerCase() === 'current_role' || i.field.toLowerCase() === 'title');

        // Filter out name and role from the rest of contact info
        const contactItems = items.filter(i =>
            i.field.toLowerCase() !== 'name' &&
            i.field.toLowerCase() !== 'role' &&
            i.field.toLowerCase() !== 'current_role' &&
            i.field.toLowerCase() !== 'title'
        );

        const getHref = (item: HeaderItem) => {
            if (item.link) return item.link;
            const lower = item.field.toLowerCase();
            if (lower.includes('email')) return `mailto:${item.value}`;
            if (lower.includes('phone')) return `tel:${item.value}`;
            return item.value.startsWith('http') ? item.value : `https://${item.value}`;
        };

        return (
            <div className="flex flex-col items-center mb-6 pb-2">
                {/* 1. Name */}
                {nameItem && (
                    <h1
                        contentEditable
                        suppressContentEditableWarning
                        className="text-2xl font-bold uppercase tracking-widest mb-0.5 outline-none focus:bg-blue-50/50 text-center"
                        onBlur={(e) => updateItemField(sIdx, items.indexOf(nameItem), 'value', e.currentTarget.textContent)}
                    >
                        {nameItem.value}
                    </h1>
                )}

                {/* 2. Role */}
                {roleItem && (
                    <div
                        contentEditable
                        suppressContentEditableWarning
                        className="text-xl text-gray-600 mb-3 font-medium outline-none focus:bg-blue-50 text-center tracking-wide"
                        onBlur={(e) => updateItemField(sIdx, items.indexOf(roleItem), 'value', e.currentTarget.textContent)}
                    >
                        {roleItem.value}
                    </div>
                )}

                {/* 3. Contact Info Row */}
                <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center items-center text-sm font-medium text-gray-700">
                    {contactItems.map((item) => {
                        const originalIndex = items.indexOf(item);
                        const displayValue = formatLinkDisplay(item.field, item.value);
                        const Icon = getIconForField(item.field);
                        const hasLink = !!item.link;
                        const href = getHref(item);

                        return (
                            <div key={item.item_id}>
                                {/* SCREEN VIEW: Editable */}
                                <div className="flex items-center gap-2 group print:hidden">
                                    <span className="text-gray-900 text-lg">
                                        {Icon}
                                    </span>

                                    <div className="flex items-center gap-1">
                                        <span
                                            contentEditable
                                            suppressContentEditableWarning
                                            className="outline-none focus:bg-blue-50 border-b border-transparent focus:border-blue-300 transition-colors"
                                            onBlur={(e) => updateItemField(sIdx, originalIndex, 'value', e.currentTarget.textContent)}
                                            title={item.value}
                                        >
                                            {displayValue}
                                        </span>

                                        {hasLink && (
                                            <a
                                                href={item.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                contentEditable={false}
                                            >
                                                <RiExternalLinkLine size={10} />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {/* PRINT VIEW: Static Link */}
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hidden print:flex items-center gap-2 text-gray-700 hover:text-black no-underline"
                                >
                                    <span className="text-gray-900 text-lg">
                                        {Icon}
                                    </span>
                                    <span>{displayValue}</span>
                                </a>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // B. paragraphs (SUMMARY)
    const renderParagraphs = (section: Section, sIdx: number) => {
        return (
            <div className="space-y-2">
                {section.items.map((item, iIdx) => {
                    const pItem = item as ParagraphItem;
                    return (
                        <div
                            key={pItem.item_id}
                            contentEditable
                            suppressContentEditableWarning
                            className="text-sm leading-relaxed text-gray-700 outline-none focus:bg-blue-50 p-1 rounded"
                            onBlur={(e) => updateItemField(sIdx, iIdx, 'text', e.currentTarget.textContent)}
                        >
                            {pItem.text}
                        </div>
                    );
                })}
            </div>
        );
    };

    // C. blocks (EXPERIENCE, PROJECTS, EDUCATION)
    const renderBlocks = (section: Section, sIdx: number) => {
        return (
            <div className="space-y-6">
                {section.items.map((item, iIdx) => {
                    const bItem = item as BlockItem;
                    return (
                        <div key={bItem.item_id} className="relative break-inside-avoid print:break-inside-avoid">
                            {/* Primary Row: Title/Company + Date */}
                            <div className="flex justify-between items-baseline mb-0.5">
                                <div className="font-bold text-lg text-gray-900 flex items-center gap-2">
                                    <span
                                        contentEditable
                                        suppressContentEditableWarning
                                        className="outline-none focus:bg-blue-50"
                                        onBlur={(e) => updateItemField(sIdx, iIdx, bItem.company ? 'company' : bItem.institution ? 'institution' : 'title', e.currentTarget.textContent)}
                                    >
                                        {bItem.company || bItem.institution || bItem.title || "Title"}
                                    </span>
                                    {renderExternalLink(bItem.link)}
                                </div>
                                <div
                                    contentEditable
                                    suppressContentEditableWarning
                                    className="text-sm font-medium text-gray-600 outline-none focus:bg-blue-50 whitespace-nowrap ml-4"
                                    onBlur={(e) => updateItemField(sIdx, iIdx, 'dates', e.currentTarget.textContent)}
                                >
                                    {bItem.dates || `${bItem.start_date || ''} - ${bItem.end_date || ''}`}
                                </div>
                            </div>

                            {/* Secondary Row: Role/Subtitle + Location */}
                            {(bItem.role_title || bItem.subtitle || bItem.location) && (
                                <div className="flex justify-between items-center mb-2">
                                    <span
                                        contentEditable
                                        suppressContentEditableWarning
                                        className="italic font-medium text-gray-800 outline-none focus:bg-blue-50"
                                        onBlur={(e) => updateItemField(sIdx, iIdx, 'role_title', e.currentTarget.textContent)}
                                    >
                                        {bItem.role_title || bItem.subtitle}
                                    </span>
                                    <span
                                        contentEditable
                                        suppressContentEditableWarning
                                        className="text-sm text-gray-500 outline-none focus:bg-blue-50"
                                        onBlur={(e) => updateItemField(sIdx, iIdx, 'location', e.currentTarget.textContent)}
                                    >
                                        {bItem.location}
                                    </span>
                                </div>
                            )}

                            {/* Tech Stack (Optional) */}
                            {bItem.tech_stack && bItem.tech_stack.length > 0 && (
                                <div className="text-sm text-gray-600 mb-2">
                                    <span className="font-semibold">Technologies: </span>
                                    {bItem.tech_stack.join(', ')}
                                    {/* Note: making this array editable is complex, usually managed via separate UI or treated as string */}
                                </div>
                            )}

                            {/* Content: Bullets OR Description */}
                            {bItem.bullets && bItem.bullets.length > 0 ? (
                                <ul className="list-disc list-outside ml-5 space-y-1">
                                    {bItem.bullets.map((bullet, bIdx) => (
                                        <li key={bullet.bullet_id} className="text-sm text-gray-700 pl-1">
                                            <span
                                                contentEditable
                                                suppressContentEditableWarning
                                                className="outline-none focus:bg-blue-50"
                                                onBlur={(e) => updateBulletText(sIdx, iIdx, bIdx, e.currentTarget.textContent || '')}
                                            >
                                                {bullet.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : bItem.description ? (
                                <div
                                    contentEditable
                                    suppressContentEditableWarning
                                    className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap outline-none focus:bg-blue-50"
                                    onBlur={(e) => updateItemField(sIdx, iIdx, 'description', e.currentTarget.textContent)}
                                >
                                    {bItem.description}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
    };

    // D. lists (SKILLS)
    const renderLists = (section: Section, sIdx: number) => {
        return (
            <div className="space-y-1">
                {section.items.map((item, iIdx) => {
                    const lItem = item as ListItem;
                    return (
                        <div key={lItem.item_id} className="text-sm leading-normal text-gray-700">
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                className="outline-none focus:bg-blue-50 p-0.5 rounded"
                                onBlur={(e) => updateItemField(sIdx, iIdx, 'text', e.currentTarget.textContent)}
                            >
                                {lItem.text}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // E. grouped_lists (SKILLS V2)
    const renderGroupedLists = (section: Section, sIdx: number) => {
        if (!section.groups) return null;

        return (
            <div className="space-y-4">
                {section.groups.map((group, gIdx) => (
                    <div key={group.group_id}>
                        <h3 className="text-sm font-bold text-gray-800 mb-2">{group.title}</h3>
                        <div className="flex flex-wrap gap-2">
                            {group.items.map((item, iIdx) => (
                                <div
                                    key={item.item_id}
                                    contentEditable
                                    suppressContentEditableWarning
                                    className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-700 outline-none focus:bg-blue-50 focus:ring-1 focus:ring-blue-300"
                                    onBlur={(e) => updateGroupItemText(sIdx, gIdx, iIdx, e.currentTarget.textContent || '')}
                                >
                                    {item.text}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // --- MAIN RENDER LOOP ---
    const renderSection = (section: Section, sIdx: number) => {
        if (section.visible === false) return null;

        return (
            <div key={section.section_id} className="mb-8 group">
                {/* Section Title (Skip for Header) */}
                {section.content_type !== 'key_value_pairs' && section.section_id && (
                    <h2 className="text-sm font-bold uppercase tracking-widest border-b border-gray-300 pb-1 mb-4 flex items-center justify-between group-hover:text-black text-gray-800">
                        {section.section_id}
                        <RiDragMoveLine className="opacity-0 group-hover:opacity-30 cursor-move text-gray-400" />
                    </h2>
                )}

                {/* Content Routing */}
                {section.content_type === 'key_value_pairs' && renderKeyValuePairs(section, sIdx)}
                {section.content_type === 'paragraphs' && renderParagraphs(section, sIdx)}
                {section.content_type === 'blocks' && renderBlocks(section, sIdx)}
                {section.content_type === 'lists' && renderLists(section, sIdx)}
                {section.content_type === 'grouped_lists' && renderGroupedLists(section, sIdx)}
            </div>
        );
    };

    return (
        <div ref={ref} className="bg-white text-black font-sans min-h-[1100px] shadow-2xl p-[50px] relative transition-all print:shadow-none print:p-[15mm] print:w-full print:h-auto print:min-h-0 print:overflow-visible resume-print-container">
            <style type="text/css" media="print">
                {`
                    @page { size: auto;  margin: 0mm; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .resume-print-container {
                        -webkit-box-decoration-break: clone;
                        box-decoration-break: clone;
                    }
                `}
            </style>
            {sections
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((section, idx) => renderSection(section, idx))
            }

            {sections.length === 0 && (
                <div className="flex flex-col items-center justify-center p-20 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl print:hidden">
                    <p>Resume is empty or invalid format.</p>
                </div>
            )}
        </div>
    );
});

export default ResumeCanvas;
